/**
 * Floating "back to top" pill — appears in the bottom-right corner of
 * any long-scroll screen (search / analytics / all-records / settings)
 * once the user has scrolled past `THRESHOLD_PX`. Tap to smoothly
 * return to the top of the page.
 *
 * The home screen has its own affordance (tapping the bottom-tab 帳本
 * cell while already on home scrolls to top), so we deliberately don't
 * render this pill on home. Callers gate visibility via the `enabled`
 * prop.
 *
 * Sharp edges:
 *   - Listens to `window` scroll, not a custom container, because the
 *     app's scroll container IS the document body (MobileShell sets
 *     `minHeight: 100dvh` only — no inner overflow). If a future screen
 *     introduces an inner scroller, that screen needs its own affordance.
 *   - We position above the tab bar (90px tall) + FAB (centered above
 *     it) so the pill never overlaps the FAB or the bottom-safe-area.
 */
import { useEffect, useState } from 'react';

import Icon from '@/components/common/Icon';

const THRESHOLD_PX = 200;

export interface BackToTopProps {
  /** When false, the pill never renders (e.g. home screen has its own
   *  tap-tab-to-top mechanism). */
  enabled?: boolean;
}

export default function BackToTop({ enabled = true }: BackToTopProps): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setVisible(false);
      return;
    }
    const onScroll = (): void => {
      setVisible(window.scrollY > THRESHOLD_PX);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled]);

  if (!enabled || !visible) return null;

  return (
    <button
      type="button"
      aria-label="回到頂端"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      style={{
        position: 'fixed',
        right: 16,
        // Stay clear of the tab bar (90px) + breathing room.
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 110px)',
        width: 44,
        height: 44,
        borderRadius: 22,
        background: 'rgba(36, 26, 16, 0.55)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        border: '0.5px solid rgba(251, 247, 241, 0.18)',
        color: 'var(--cream-50)',
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        boxShadow: '0 4px 14px rgba(36, 26, 16, 0.25)',
        zIndex: 25,
      }}
    >
      <Icon name="chevR" size={22} stroke="currentColor" style={{ transform: 'rotate(-90deg)' }} />
    </button>
  );
}
